import React from 'react';
import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { TextField, Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import { useCreateOffice } from '../../../hooks/useCreateOffice';
import { useUpdateOffice } from '../../../hooks/useUpdateOffice';
import { useSnackbar } from '../../../context/SnackbarContext';
import CompanySelect from '../controls/CompanySelect';
import { useTranslation } from 'react-i18next';

export default function OfficeForm({ setOffices, office = null, mode = "create", open, onClose }) {

    const { t } = useTranslation('common'); 

    const [name, setName] = React.useState(office ? office.name : "");
    const [allowedBytes, setAllowedBytes] = React.useState(office ? office.allowed_bytes : "");
    const [currentBytes, setCurrentBytes] = React.useState(office ? office.current_bytes : 0);
    
    const [selectedCompany, setSelectedCompany] = useState(office ? office.company.id : "");

    const { create } = useCreateOffice(setOffices);
    const { update } = useUpdateOffice(setOffices);

    const { showSnackbar } = useSnackbar();

    React.useEffect(() => {
        setName(office ? office.name : "");
        setAllowedBytes(office ? office.allowed_bytes : "");
        setCurrentBytes(office ? office.current_bytes : 0);
        setSelectedCompany(office ? office.company.id : "");
    }, [office, open]);

    const handleSave = async () => {
        if (!name.trim()) {
            showSnackbar(t('offices.messages.name_required'), "warning");
            return;
        }
        if (!allowedBytes) {
            showSnackbar(t('offices.messages.allowed_bytes_required'), "warning");
            return;
        }
        if (!selectedCompany) {
            showSnackbar(t('offices.messages.company_required'), "warning");
            return;
        }

        try {
            if (mode === "create") {
                await create({ 
                    name: name,
                    allowed_bytes: allowedBytes,
                    company_id: selectedCompany
                });
                showSnackbar(t('offices.messages.create_success'), "success");
            } else if (mode === "edit" && office) {
                await update(office.id, { 
                    name: name,
                    allowed_bytes: allowedBytes,
                    company_id: selectedCompany 
                });
                showSnackbar(t('offices.messages.update_success'), "success");
            }
            setName("");   // clear field after save
            setAllowedBytes("");   // clear field after save
            setCurrentBytes(0);   // clear field after save
            setSelectedCompany("");
            onClose();     // close dialog
            } catch (err) {
            showSnackbar(t('offices.messages.save_fail'), "error");
        }

    };

    const handleCancel = () => {
        setName("");   // clear field on cancel
        setAllowedBytes("");   // clear field after save
        setCurrentBytes(0);
        setSelectedCompany("");
        onClose();
    };

    return (
        <Dialog onClose={handleCancel} open={open} disableRestoreFocus>
        <DialogTitle>{mode === "create" ? "Add Office" : "Edit Office"}</DialogTitle>

        <DialogContent dividers>
            <Stack spacing={1}>
                <CompanySelect
                    value={selectedCompany}
                    onChange={(e) => setSelectedCompany(e.target.value)}
                />
                <TextField
                    label="Office Name"
                    variant="outlined"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                />
                <TextField
                    type='number'
                    label="Allowed Bytes"
                    variant="outlined"
                    value={allowedBytes}
                    onChange={(e) => setAllowedBytes(e.target.value)}
                    fullWidth
                />
                <Typography variant='body2' >{`Current: ${currentBytes}`}</Typography>
            </Stack>
        </DialogContent>

        <DialogActions>
            <Button onClick={handleSave}>Save</Button>
            <Button onClick={handleCancel}>Cancel</Button>
        </DialogActions>
        </Dialog>
    );
}